<template>
  <div class="page">
    <!-- 頂部標題 -->
    <div class="header">
      <div class="hospital-title">基督教聯合醫院</div>
      <div class="system-name">
        <i class="fa fa-car"></i>
        停車場即時資訊系統
      </div>
    </div>
    <!-- A座停車場 -->
    <div class="parking-card" id="blockA">
      <div class="parking-title">A座 M/F停車場</div>
      <div class="parking-eng">Block A M/F Carpark</div>
      <div class="car-icon">
        <i class="fa fa-car"></i>
      </div>
      <div class="available-count" :class="{ 'update-pulse': pulseA && maintenanceFlags.block_a === false, 'maintenance-label': maintenanceFlags.block_a === true }">
        {{ displayAvailable('block_a', aAvailable) }}
      </div>
    </div>

    <!-- S座停車場（含私家車/電單車） -->
    <div class="parking-card" id="blockS">
      <div class="parking-title">S座 B4-B5/F停車場</div>
      <div class="parking-eng">Block S B4-B5/F Carpark</div>

      <!-- 私家車車位 -->
      <div class="car-icon">
        <i class="fa fa-car"></i>
      </div>
      <div class="available-count" :class="{ 'update-pulse': pulseSCar && maintenanceFlags.block_s_car === false, 'maintenance-label': maintenanceFlags.block_s_car === true }">
        {{ displayAvailable('block_s_car', sCarAvailable) }}
      </div>

      <!-- 分隔線 -->
      <div class="divider"></div>

      <!-- 電單車車位 -->
      <div class="car-icon">
        <i class="fa fa-motorcycle"></i>
      </div>
      <div class="available-count" :class="{ 'update-pulse': pulseSBike && maintenanceFlags.block_s_motorcycle === false, 'maintenance-label': maintenanceFlags.block_s_motorcycle === true }">
        {{ displayAvailable('block_s_motorcycle', sBikeAvailable) }}
      </div>
    </div>
    <!-- K座停車場 -->
    <div class="parking-card" id="blockK">
      <div class="parking-title">K座 停車場</div>
      <div class="parking-eng">BLOCK K CARPARK</div>
      <div class="car-icon">
        <i class="fa fa-car"></i>
      </div>
      <div class="available-count" :class="{ 'update-pulse': pulseK && maintenanceFlags.block_k === false, 'maintenance-label': maintenanceFlags.block_k === true }">
        {{ displayAvailable('block_k', kAvailable) }}
      </div>
    </div>
    <div class="disclaimer">
      此系統數據會有10秒延遲,如對任何由此系統所載資料所產生或與之相關的損失或損害，本院概不承任何責任。The
      Hospital is not responsible for any loss or damage arising from or in
      connection with any information provided by this system.
    </div>
    <!-- 底部系統狀態 -->
    <div class="footer">
      <div class="system-status">
        系統狀態：<span :class="systemStatusClass">{{ systemStatusText }}</span>
      </div>
      <div class="current-time">{{ currentTime }}</div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import axios from "axios";
import {
  DEFAULT_MAINTENANCE_API_URL,
  maintenanceDisplay,
  startMaintenanceSync,
} from "../utils/maintenance";

export default defineComponent({
  name: "CarView",
  data() {
    return {
      // 數據
      kAvailable: 0,
      aAvailable: 0,
      sCarAvailable: 0,
      sBikeAvailable: 0,

      // null 表示尚未取得有效維修狀態，避免誤顯示正常車位數。
      maintenanceFlags: { block_a: null, block_s_car: null, block_s_motorcycle: null, block_k: null },
      maintenanceSyncStatus: "pending",
      maintenanceSync: null,

      // 狀態與時間
      currentTime: "",

      // 動畫控制
      pulseK: false,
      pulseA: false,
      pulseSCar: false,
      pulseSBike: false,

      // 定時器
      timerData: null,
      timerClock: null,

      // WebSocket
      wsUrl: "wss://car.haiotsys.com:8099",
      ws: null,
      reconnectAttempts: 0,
      reconnectTimer: null,
      heartbeatTimer: null,
      wsConnected: false,
      isReconnecting: false,
      isUnmounted: false,
    };
  },
  computed: {
    systemStatusText() {
      if (this.maintenanceSyncStatus === "pending") return "維修狀態待同步";
      if (this.maintenanceSyncStatus === "error") return "維修狀態同步異常";
      return Object.values(this.maintenanceFlags).some((flag) => flag === true)
        ? "停車場維修中"
        : "正常運作";
    },
    systemStatusClass() {
      return this.maintenanceSyncStatus !== "synced" ||
        Object.values(this.maintenanceFlags).some((flag) => flag === true)
        ? "status-warning"
        : "status-normal";
    },
  },
  mounted() {
    // 時鐘
    this.updateClock();
    this.timerClock = setInterval(this.updateClock, 1000);
    this.initMaintenanceSync();

    // 若未配置 WS 地址，暫用輪詢作為臨時回退
    if (!this.wsUrl) {
      this.refreshData();
      this.timerData = setInterval(this.refreshData, 5000);
    } else {
      this.refreshData();
      this.timerData = setInterval(this.refreshData, 5000);
      this.initWebSocket();
    }

    // S座初次進場動畫
    this.triggerPulse("pulseSCar");
    this.triggerPulse("pulseSBike");
  },
  beforeUnmount() {
    this.isUnmounted = true;
    if (this.maintenanceSync) this.maintenanceSync.stop();
    if (this.timerData) clearInterval(this.timerData);
    if (this.timerClock) clearInterval(this.timerClock);
    this.teardownWebSocket();
  },
  methods: {
    displayAvailable(blockId, count) {
      return maintenanceDisplay(this.maintenanceFlags, blockId, count);
    },
    initMaintenanceSync() {
      const url =
        process.env.VUE_APP_MAINTENANCE_API_URL || DEFAULT_MAINTENANCE_API_URL;
      let storage = null;
      try {
        storage = window.localStorage;
      } catch (error) {
        // 部分屏幕瀏覽器停用了本地存儲，仍可正常同步。
      }
      this.maintenanceSync = startMaintenanceSync({
        url,
        storage,
        request: (requestUrl) => {
          const source = axios.CancelToken.source();
          return {
            promise: axios.get(requestUrl, {
              timeout: 4000,
              cancelToken: source.token,
            }).then((response) => response.data),
            cancel: () => source.cancel("Screen unmounted"),
          };
        },
        onSnapshot: (snapshot) => {
          this.maintenanceFlags = snapshot.flags;
        },
        onStatus: (status) => {
          this.maintenanceSyncStatus = status;
        },
      });
    },
    updateClock() {
      this.currentTime = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    triggerPulse(flagKey) {
      this[flagKey] = false;
      // 下一輪設置為 true 以觸發動畫
      requestAnimationFrame(() => {
        this[flagKey] = true;
        setTimeout(() => {
          this[flagKey] = false;
        }, 500);
      });
    },
    async fetchKAvailable() {
      try {
        const response = await axios.get("/api/count");
        const nextValue = response?.data?.result?.k_available;
        if (typeof nextValue === "number") {
          if (nextValue !== this.kAvailable) {
            this.kAvailable = nextValue;
            this.triggerPulse("pulseK");
          }
        } else {
          // 返回不符合預期時忽略更新
          // console.warn('Unexpected /api/count response:', response?.data)
        }
      } catch (err) {
        // console.error('Error calling /api/count', err)
      }
    },
    async fetchAAvailable() {
      try {
        const response = await axios.get("/api/count");
        const nextValue = response?.data?.result?.a_available;
        if (typeof nextValue === "number") {
          if (nextValue !== this.aAvailable) {
            this.aAvailable = nextValue;
            this.triggerPulse("pulseA");
          }
        } else {
          // 返回不符合預期時忽略更新
          // console.warn('Unexpected /api/count response:', response?.data)
        }
      } catch (err) {
        // console.error('Error calling /api/count', err)
      }
    },
    refreshData() {
      this.fetchKAvailable();
      this.fetchAAvailable();
    },

    // ===== WebSocket 邏輯 =====
    initWebSocket() {
      if (!this.wsUrl) return;
      console.info("[WS] initWebSocket with url:", this.wsUrl);
      this.createWebSocket();
    },
    createWebSocket() {
      if (this.isUnmounted) return;
      try {
        if (this.ws) {
          try {
            this.ws.close();
          } catch (e) {}
        }
        console.info("[WS] connecting to", this.wsUrl);
        const socket = new WebSocket(this.wsUrl);
        this.ws = socket;

        socket.onopen = () => {
          console.info("[WS] open");
          this.reconnectAttempts = 0;
          this.wsConnected = true;
          this.isReconnecting = false;
          this.startHeartbeat();
          // 可在此發送訂閱消息（具體由後端定義）
        };

        socket.onmessage = (event) => {
          try {
            // console.log(
            //   "[WS] message raw:",
            //   typeof event.data === "string" ? event.data : event.data
            // );
          } catch (e) {}
          this.handleWsMessage(event.data);
        };

        socket.onerror = (evt) => {
          console.error("[WS] error event:", evt);
          // 等 onclose 走重連
        };

        socket.onclose = (evt) => {
          console.warn("[WS] close", {
            code: evt.code,
            reason: evt.reason,
            wasClean: evt.wasClean,
          });
          this.stopHeartbeat();
          this.wsConnected = false;
          this.scheduleReconnect();
        };
      } catch (e) {
        console.error("[WS] create error, will reconnect:", e);
        this.scheduleReconnect();
      }
    },
    handleWsMessage(raw) {
      let payload = raw;
      // console.log("[WS] raw message:", raw);

      if (typeof raw === "string") {
        try {
          payload = JSON.parse(raw);
          // console.log("[WS] message parsed:", payload);
        } catch (e) {
          console.log("[WS] message non-JSON:", raw);
        }
      } else {
        // console.log("[WS] message object:", payload);
      }

      // 處理新的 carpark=X，motorpark=Y 格式
      if (typeof payload === "string") {
        const parkingPattern = /carpark=(\d+)(?:，|,)\s*motorpark=(\d+)/i;
        const match = payload.trim().match(parkingPattern);
        console.log("[WS] match:-=-=--", match);
        if (match) {
          const carparkValue = Number(match[1]);
          const motorparkValue = Number(match[2]);

          console.info("[WS] Parking data parsed:", {
            carpark: carparkValue,
            motorpark: motorparkValue,
          });

          // 更新私家車車位（carpark）
          if (
            Number.isFinite(carparkValue) &&
            carparkValue !== this.sCarAvailable
          ) {
            this.sCarAvailable = carparkValue;
            this.triggerPulse("pulseSCar");
            console.info("[WS] S car (carpark) ->", this.sCarAvailable);
          }

          // 更新電單車車位（motorpark）
          if (
            Number.isFinite(motorparkValue) &&
            motorparkValue !== this.sBikeAvailable
          ) {
            this.sBikeAvailable = motorparkValue;
            this.triggerPulse("pulseSBike");
            console.info("[WS] S bike (motorpark) ->", this.sBikeAvailable);
          }

          return;
        }

        // 向下兼容：若無法匹配新格式，嘗試解析為純數字（舊邏輯）
        const v = Number(payload.trim());
        console.log("[WS] v:-=-=--", v);

        if (Number.isFinite(v)) {
          if (v !== this.sCarAvailable) {
            this.sCarAvailable = v;
            this.triggerPulse("pulseSCar");
            console.info(
              "[WS] S car (numeric string, legacy) ->",
              this.sCarAvailable,
            );
          }
          return;
        }
      }

      // 若服務端直接推送數字，直接作為 S 座私家車位數（向下兼容）
      if (typeof payload === "number" && Number.isFinite(payload)) {
        const v = Number(payload);
        if (v !== this.sCarAvailable) {
          this.sCarAvailable = v;
          this.triggerPulse("pulseSCar");
          console.info("[WS] S car (numeric, legacy) ->", this.sCarAvailable);
        }
        return;
      }

      // 心跳回應
      if (payload && (payload.type === "pong" || payload === "pong")) {
        console.debug("[WS] pong received");
        return;
      }

      // 處理 type == "carnum" 的消息（A座停車位）
      if (payload && payload.type === "carnum") {
        const vacancy = payload.Vacancy;
        if (typeof vacancy === "number" && Number.isFinite(vacancy)) {
          if (vacancy !== this.aAvailable) {
            this.aAvailable = vacancy;
            this.triggerPulse("pulseA");
            // console.info("[WS] A座 (carnum) updated ->", this.aAvailable);
          }
        }
        return;
      }

      // 兼容不同字段命名與包裹層級
      const data = payload && payload.result ? payload.result : payload;
      console.log("[WS] data to apply:", data);
      if (!data || typeof data !== "object") return;

      const maybeK = data.k_available ?? data.kAvailable ?? data.k ?? null;
      const maybeA = data.a_available ?? data.aAvailable ?? data.a ?? null;
      const maybeSCar =
        data.s_car_available ??
        data.sCarAvailable ??
        data.s_car ??
        data.carpark ??
        null;
      const maybeSBike =
        data.s_bike_available ??
        data.sBikeAvailable ??
        data.s_bike ??
        data.motorpark ??
        null;
      console.log("[WS] extracted:", {
        k: maybeK,
        a: maybeA,
        sCar: maybeSCar,
        sBike: maybeSBike,
      });

      if (Number.isFinite(maybeK)) {
        if (maybeK !== this.kAvailable) {
          this.kAvailable = Number(maybeK);
          this.triggerPulse("pulseK");
          console.info("[WS] K updated ->", this.kAvailable);
        }
      }
      if (Number.isFinite(maybeA)) {
        if (maybeA !== this.aAvailable) {
          this.aAvailable = Number(maybeA);
          this.triggerPulse("pulseA");
          console.info("[WS] A updated ->", this.aAvailable);
        }
      }
      if (Number.isFinite(maybeSCar)) {
        if (maybeSCar !== this.sCarAvailable) {
          this.sCarAvailable = Number(maybeSCar);
          this.triggerPulse("pulseSCar");
          console.info("[WS] S car updated ->", this.sCarAvailable);
        }
      }
      if (Number.isFinite(maybeSBike)) {
        if (maybeSBike !== this.sBikeAvailable) {
          this.sBikeAvailable = Number(maybeSBike);
          this.triggerPulse("pulseSBike");
          console.info("[WS] S bike updated ->", this.sBikeAvailable);
        }
      }
    },
    scheduleReconnect() {
      if (this.isUnmounted || !this.wsUrl) return;
      if (this.reconnectTimer) return;
      const base = 1000;
      const factor = 1.8;
      const max = 30000;
      const delay = Math.min(
        max,
        Math.floor(base * Math.pow(factor, this.reconnectAttempts)),
      );
      console.warn("[WS] schedule reconnect:", {
        attempt: this.reconnectAttempts + 1,
        delay,
      });
      this.reconnectAttempts += 1;
      this.isReconnecting = true;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.createWebSocket();
      }, delay);
    },
    startHeartbeat() {
      this.stopHeartbeat();
      console.info("[WS] heartbeat started");
      this.heartbeatTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          try {
            this.ws.send(JSON.stringify({ type: "ping" }));
            console.debug("[WS] ping sent");
          } catch (e) {}
        }
      }, 20000);
    },
    stopHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
        console.info("[WS] heartbeat stopped");
      }
    },
    teardownWebSocket() {
      this.stopHeartbeat();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.ws) {
        try {
          this.ws.onopen = null;
          this.ws.onmessage = null;
          this.ws.onerror = null;
          this.ws.onclose = null;
          this.ws.close();
        } catch (e) {}
        this.ws = null;
      }
    },
  },
});
</script>

<style scoped>
/* 頁面（替代 body 鎖定 1080×1920） */
.page {
  background-color: #0b0f19;
  color: #fff;
  width: 1080px;
  height: 1920px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans CJK SC", "Noto Sans",
    "Helvetica Neue", Arial, sans-serif;
  transform-origin: top center;
}

/* 頂部標題區 */
.header {
  background: linear-gradient(to bottom, #1b3b70, #0b0f19);
  padding: 50px 40px;
  border-bottom: 3px solid #1f4c8c;
  text-align: center;
}
.hospital-title {
  font-size: 88px;
  font-weight: bold;
  margin-bottom: 25px;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
}
.system-name {
  font-size: 60px;
  color: #86c8ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.system-name i {
  font-size: 64px;
  margin-right: 20px;
}

/* 停車場卡片通用樣式 */
.parking-card {
  background: #101a2b;
  border-radius: 25px;
  padding: 35px;
  margin: 30px 50px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(31, 76, 140, 0.3);
}

.parking-title {
  font-size: 72px;
  font-weight: bold;
  margin-bottom: 15px;
  width: 100%;
  color: #e6efff;
}
.parking-eng {
  font-size: 44px;
  color: #8899aa;
  margin-bottom: 30px;
  width: 100%;
}
.car-icon {
  font-size: 105px;
  color: #66b3ff;
  text-align: center;
  width: 200px;
  text-shadow: 0 2px 10px rgba(102, 179, 255, 0.3);
}
.available-count {
  font-size: 140px;
  font-weight: bold;
  color: #fff;
  text-align: center;
  width: 380px;
  text-shadow: 0 3px 10px rgba(0, 0, 0, 0.5);
  transition: all 0.5s ease;
}
.available-count.maintenance-label {
  font-size: 100px;
  white-space: nowrap;
}

/* 車位類型分隔線 */
.divider {
  width: 100%;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(102, 179, 255, 0.3),
    transparent
  );
  margin: 35px 0;
}

/* 底部系統狀態 */
.footer {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #0b0f19;
  border-top: 2px solid #1f4c8c;
  padding: 30px 0px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  font-size: 42px;
}
.system-status {
  color: #99aabb;
}
.status-normal {
  color: #28a745;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
}
.status-warning {
  color: #ffc107;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(255, 193, 7, 0.3);
}
.current-time {
  color: #66b3ff;
  text-shadow: 0 0 10px rgba(102, 179, 255, 0.2);
}

/* 免责声明（左下角固定） */
.disclaimer {
  position: absolute;
  left: 66px;
  bottom: 120px;
  max-width: 90%;
  color: #99aabb;
  line-height: 1;
  opacity: 0.9;
  font-size: 18px;
}

/* 數據更新動畫 */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}
.update-pulse {
  animation: pulse 0.5s ease;
}

/* 響應式適配 - 平板設備 */
@media (max-width: 1079px) and (min-width: 769px) {
  .page {
    width: 100vw;
    height: auto;
    min-height: 100vh;
    transform: scale(calc(100vw / 1080));
  }
}

/* 響應式適配 - 手機設備 */
@media (max-width: 768px) {
  .page {
    width: 100vw;
    height: auto;
    min-height: 100vh;
    transform: none;
    overflow-y: auto;
  }

  /* 頂部標題區 - 手機優化 */
  .header {
    padding: 30px 20px;
  }

  .hospital-title {
    font-size: 48px;
    margin-bottom: 15px;
  }

  .system-name {
    font-size: 32px;
  }

  .system-name i {
    font-size: 36px;
    margin-right: 10px;
  }

  /* 停車場卡片 - 手機優化 */
  .parking-card {
    border-radius: 15px;
    padding: 25px;
    margin: 25px 15px;
  }

  .parking-title {
    font-size: 42px;
    margin-bottom: 10px;
  }

  .parking-eng {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .car-icon {
    font-size: 60px;
    width: 110px;
  }

  .available-count {
    font-size: 80px;
    width: 210px;
  }
  .available-count.maintenance-label {
    font-size: 56px;
  }

  /* 分隔線 - 手機優化 */
  .divider {
    margin: 20px 0;
  }

  /* 底部系統狀態 - 手機優化 */
  .footer {
    padding: 20px 10px;
    font-size: 24px;
    flex-direction: column;
    gap: 10px;
  }

  /* 免責聲明 - 手機優化 */
  .disclaimer {
    position: relative;
    left: 0;
    bottom: auto;
    max-width: 100%;
    padding: 20px 15px;
    margin-bottom: 120px;
    font-size: 12px;
    line-height: 1.4;
    text-align: center;
  }
}

/* 響應式適配 - 小屏手機 */
@media (max-width: 480px) {
  .hospital-title {
    font-size: 36px;
  }

  .system-name {
    font-size: 24px;
  }

  .parking-title {
    font-size: 32px;
  }

  .parking-eng {
    font-size: 18px;
  }

  .car-icon {
    font-size: 48px;
    width: 90px;
  }

  .available-count {
    font-size: 64px;
    width: 180px;
  }
  .available-count.maintenance-label {
    font-size: 48px;
  }

  .footer {
    font-size: 20px;
  }

  .disclaimer {
    font-size: 10px;
  }
}
</style>
